import { useMemo } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import { useGetMeshDocumentQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';

export const useMeshDocument = (
  meshDocumentId: string | null,
): {
  meshDocument: DataPipeline.MeshDocument | null;
  loading: boolean;
  error?: Error;
} => {
  const { data, loading, error } = useGetMeshDocumentQuery({
    skip: !meshDocumentId,
    variables: {
      meshDocumentId: meshDocumentId ?? '',
    },
  });

  const meshDocument = useMemo(() => {
    if (!data?.meshDocument) {
      return null;
    }

    return DbGraphUtils.deserializeMeshDocument(data.meshDocument);
  }, [data?.meshDocument]);

  // Apollo only populates `error` on network/GraphQL failures, not when the
  // query resolves with a null body. A requested-but-missing mesh document
  // would otherwise leave loading=false, error=undefined, meshDocument=null,
  // which callers read as "still loading" and freeze the UI. Surface it as an
  // explicit not-found error instead.
  const notFoundError =
    meshDocumentId && !loading && !error && !data?.meshDocument
      ? new Error(`Mesh document not found [meshDocumentId:${meshDocumentId}]`)
      : undefined;

  return {
    meshDocument,
    loading,
    error: error ?? notFoundError,
  };
};
